import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { treatmentTypeApi } from '../api';
import type { TreatmentTypeFormData, TreatmentTypeListFilters } from '../types';

const KEYS = {
  all: ['treatmentTypes'] as const,
  lists: () => [...KEYS.all, 'list'] as const,
  list: (filters: TreatmentTypeListFilters) => [...KEYS.lists(), filters] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export function useTreatmentTypes(filters: TreatmentTypeListFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: async () => {
      const { data } = await treatmentTypeApi.list(filters);
      return data;
    },
  });
}

export function useTreatmentTypeDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await treatmentTypeApi.getById(id!);
      return data.treatmentType;
    },
    enabled: !!id,
  });
}

export function useCreateTreatmentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: TreatmentTypeFormData) => {
      const { data } = await treatmentTypeApi.create(formData);
      return data.treatmentType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateTreatmentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TreatmentTypeFormData> }) => {
      const { data: response } = await treatmentTypeApi.update(id, data);
      return response.treatmentType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeactivateTreatmentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await treatmentTypeApi.deactivate(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useSetTreatmentTypeHelmets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ treatmentTypeId, helmetTypeIds }: { treatmentTypeId: string; helmetTypeIds: string[] }) => {
      const { data } = await treatmentTypeApi.setHelmetConnections(treatmentTypeId, helmetTypeIds);
      return data.treatmentType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
      queryClient.refetchQueries({ queryKey: ['helmetTypes'] });
    },
  });
}
